<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import './_index.scss'

import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import RoomieCardLink from '@lahaus-roomie/roomie/src/components/RoomieCardLink/index.vue'
import type { Props } from './types'
import { hasBody, hasOutstandingRecommendation, hasPassiveRecommendations, hasSuggestedMessages, hasRecommendations, hasPassiveRecommendationsWithImages } from './utils'
import { omit } from '@/utils/objects'
import type { OutstandingRecommendation, PassiveRecommendationsWithImages, SuggestedMessageItem, SuggestedMessages } from '@/composables/useSamGpt/types'
import AppBubbleAuthor from '@/components/App/Chat/BubbleAuthor/index.vue'
import AppAiChatRecommendationCard from '@/components/App/Ai/Chat/RecommendationCard/index.vue'
import AppAiChatPassiveRecommendation from '@/components/App/Ai/Chat/PassiveRecommendation/index.vue'
import AppAiChatPassiveRecommendationWithImages from '@/components/App/Ai/Chat/PassiveRecommendationWithImages/index.vue'
import AppChatRecommendations from '@/components/App/Chat/Recommendations/index.vue'

const { locale } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  initOffline: false
})
const emit = defineEmits(['change-message-list', 'suggested-message-click', 'passive-recommendation-click', 'outstanding-recommendation-click'])

const trackRef = ref<HTMLElement | null>(null)

const baseListingInfo = {
  countryCode: locale?.value?.toLocaleUpperCase()
}

const { init, messages, showWritingFeedback, handleSendMessage, disableInputField } = useSamGpt({ screen: props.screen, screenCta: props.screenCta, listingInfo: baseListingInfo })

const isBotWriting = computed(() => showWritingFeedback.value)

const handleSuggestedMessageClick = async (suggestedMessage: SuggestedMessageItem, suggestedMessages: SuggestedMessages) => {
  suggestedMessage.active = !suggestedMessage.active

  const mergedMessage = {
    ...omit(suggestedMessages, 'messages'),
    ...suggestedMessage
  }

  await handleSendMessage(suggestedMessage.label, mergedMessage)

  emit('suggested-message-click', mergedMessage)
}

const handleOutstandingRecommendationClick = (outstandingRecommendation: OutstandingRecommendation) => {
  emit('outstanding-recommendation-click', outstandingRecommendation.card_params)
}

const handlePassiveRecommendationClick = (actionableValue: any) => {
  emit('passive-recommendation-click', actionableValue)
}

const handlePassiveRecommendationWithImagesClick = (passiveRecommendation: PassiveRecommendationsWithImages) => {
  emit('passive-recommendation-click', passiveRecommendation)
}

let isInitialScroll = true

const scrollToBottom = () => {
  if (!trackRef.value) return

  trackRef.value.scrollTo({
    top: trackRef.value.scrollHeight,
    behavior: !isInitialScroll ? 'smooth' : 'auto'
  })

  isInitialScroll = false
}

watch([() => messages.value.length, isBotWriting], () => {
  emit('change-message-list', messages.value)
  scrollToBottom()
}, { flush: 'post' })

onMounted(async () => {
  const opts: any = {}

  if (props.initOffline && props.offlineAgent) {
    opts.offline = true
    opts.offlineAgent = props.offlineAgent
  }

  await init(opts)
})

defineExpose({
  handleSendMessage,
  disableInputField
})
</script>

<script lang="ts">
export default {
  name: 'AppAiChatBubbles'
}
</script>

<template>
  <div
    ref="trackRef"
    class="chat-bubbles h-full flex flex-col overflow-y-scroll gap-24">
    <template
      v-for="message, index in messages"
      :key="index">
      <div
        v-if="hasBody(message)"
        :class="`chat-bubbles__message chat-bubbles__message--${message.author}`">
        <AppBubbleAuthor
          :author="message.author"
          bot-name="Sam"
          bot-picture-type="hidden" />

        <div class="chat-bubbles__bubble w-fit hyphens-auto shadow-[0_0_1px_#0000002f]">
          <p v-sanitize="message.body" />
        </div>

        <template v-if="hasSuggestedMessages(message)">
          <div class="chat-bubbles__chips-container flex w-full flex-col gap-8">
            <RoomieCardLink
              v-for="suggestedMessage in message.suggestedMessages.messages"
              :id="`chat-bubbles-chip-selector-${message.suggestedMessages.reference}-${suggestedMessage.value}`"
              :key="`chat-bubbles-chip-selector-${suggestedMessage.value}`"
              :data-lh-id="`chat-bubbles-chip-selector-${message.suggestedMessages.reference}-${suggestedMessage.value || 'all'}`"
              class="cursor-pointer"
              :class="{ selected: suggestedMessage.active }"
              container-tag="div"
              :title="suggestedMessage.label"
              :icon-name="suggestedMessage.icon"
              :interactive="true"
              :can-right-arrow="true"
              @click="() => handleSuggestedMessageClick(suggestedMessage, message.suggestedMessages)" />

            <div
              v-if="message.suggestedMessages.skip_option"
              class="w-full flex justify-center">
              <RoomieButton
                :id="`chat-bubbles-chip-selector-${message.suggestedMessages.reference}-skip`"
                :data-lh-id="`chat-bubbles-chip-selector-${message.suggestedMessages.reference}-skip`"
                type="button"
                :aria-label="message.suggestedMessages.skip_option.label"
                variant="ghost"
                @click="() => handleSuggestedMessageClick(message.suggestedMessages.skip_option, message.suggestedMessages)">
                {{ message.suggestedMessages.skip_option.label }}
              </RoomieButton>
            </div>
          </div>
        </template>

        <template v-if="hasOutstandingRecommendation(message)">
          <AppAiChatRecommendationCard
            id="chat-bubbles-outstanding-recommendation"
            :name="message.outstandingRecommendation.card_params.name"
            :min-price="message.outstandingRecommendation.card_params.min_price"
            :currency="message.outstandingRecommendation.card_params.currency"
            :cover-image="message.outstandingRecommendation.card_params.cover_image"
            class="mt-18 cursor-pointer"
            data-lh-id="chat-bubbles-outstanding-recommendation"
            @click="() => handleOutstandingRecommendationClick(message.outstandingRecommendation)" />
        </template>

        <template v-if="hasPassiveRecommendations(message)">
          <AppAiChatPassiveRecommendation
            :reference="message.passiveRecommendations.reference"
            :fragments="message.passiveRecommendations.text_fragments"
            class="mt-18 w-11/12"
            @actionable-click="handlePassiveRecommendationClick" />
        </template>

        <template v-if="hasPassiveRecommendationsWithImages(message)">
          <AppAiChatPassiveRecommendationWithImages
            :recommendations="message.passiveRecommendationsWithImages"
            class="mt-18"
            @actionable-click="handlePassiveRecommendationWithImagesClick" />
        </template>

        <template v-if="hasRecommendations(message)">
          <AppChatRecommendations
            class="mt-8"
            :listings="message.recommendations"
            :screen />
        </template>
      </div>
    </template>

    <div
      v-if="isBotWriting"
      class="chat-bubbles__message chat-bubbles__message--bot text-14 text-primary-600 font-medium">
      <div class="dot-loader my-24 mx-6" />
    </div>
  </div>
</template>
